import ModalViewModel from 'infra/ModalViewModel'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import JobStore, { FinalReturnYearInput } from 'jobs/JobStore'
import Job from 'jobs/Job'
import { AlertFactory } from 'alerts/Alert'

let entryIdCounter = 0

/**
 * A single per-state signed amount within a tax year block.
 */
export class StateAmountEntry {
  readonly id = ++entryIdCounter
  @observable state = ''
  @observable amount = 0

  constructor(state = '', amount = 0) {
    makeObservable(this)
    this.state = state
    this.amount = amount
  }

  @computed
  get isCompleted() {
    return this.state !== ''
  }
}

/**
 * A single tax year block: signed federal amount + per-state signed amounts.
 */
export class YearAmountEntry {
  readonly id = ++entryIdCounter
  @observable taxYear = ''
  @observable federalAmount = 0
  @observable stateEntries: StateAmountEntry[] = []

  constructor(
    taxYear = '',
    federalAmount = 0,
    stateEntries: StateAmountEntry[] = [],
  ) {
    makeObservable(this)
    this.taxYear = taxYear
    this.federalAmount = federalAmount
    this.stateEntries = stateEntries
  }

  @computed
  get hasIncompleteStates() {
    return this.stateEntries.some((e) => !e.isCompleted)
  }

  @computed
  get isCompleted() {
    return this.taxYear !== '' && !this.hasIncompleteStates
  }

  @action.bound
  addStateEntry() {
    this.stateEntries.push(new StateAmountEntry())
  }

  @action.bound
  removeStateEntry(index: number) {
    this.stateEntries.splice(index, 1)
  }
}

/**
 * Dialog for entering per-year final return amounts when a Pro marks a
 * RETAIL return as filed after uploading a Final Return document.
 *
 * Amounts are signed: positive = refund to client, negative = owed.
 */
export class FinalReturnAmountsDialogViewModel extends ModalViewModel {
  constructor(
    private jobStore: JobStore,
    private job: Job,
    private newAlert: AlertFactory,
    private onComplete: () => Promise<void>,
  ) {
    super()
    makeObservable(this)
    this.close$.subscribe(this.reset)
  }

  @observable
  yearEntries: YearAmountEntry[] = [new YearAmountEntry(this.defaultTaxYear)]

  @computed
  get hasIncompleteEntries() {
    return (
      this.yearEntries.length === 0 ||
      this.yearEntries.some((y) => !y.isCompleted)
    )
  }

  /**
   * Transitional default tax year, mirroring the backend's `TaxYear.Default`
   * (max(job.years) - 1). Until documents-api exposes a real `tax_year` and the
   * read model surfaces `refund_amounts_by_year`, we default to the previous
   * calendar year.
   *
   * TODO: prefill from the read model once it exposes refund_amounts_by_year.
   */
  private get defaultTaxYear() {
    return String(new Date().getFullYear() - 1)
  }

  /**
   * Selectable tax years for the dropdown: the current calendar year down
   * through the previous ten (late filings and amendments reach back years).
   */
  get taxYearOptions(): [string, string][] {
    const current = new Date().getFullYear()
    return Array.from({ length: 11 }, (_, i) => {
      const year = String(current - i)
      return [year, year]
    })
  }

  @action.bound
  addYearEntry() {
    this.yearEntries.push(new YearAmountEntry(this.defaultTaxYear))
  }

  @action.bound
  removeYearEntry(index: number) {
    this.yearEntries.splice(index, 1)
  }

  /**
   * Restores the single default-year entry so the next open starts fresh.
   */
  @action.bound
  reset() {
    this.yearEntries = [new YearAmountEntry(this.defaultTaxYear)]
  }

  submit = task.resolved(async () => {
    if (this.hasIncompleteEntries) {
      return
    }

    const years: FinalReturnYearInput[] = this.yearEntries.map((y) => ({
      taxYear: y.taxYear,
      federalAmount: y.federalAmount,
      stateAmounts: y.stateEntries.map((s) => ({
        state: s.state,
        amount: s.amount,
      })),
    }))

    try {
      await this.jobStore.recordJobReturnAmounts(this.job.id, years)
      await this.onComplete()
      this.close()
    } catch (err) {
      this.newAlert().fromError(err).show()
    }
  })
}
